<template>
  <div class="timeline-container">
    <div class="timeline-header">
      <h2 class="timeline-title">{{ $t('YourJourney') }}</h2>
      <div class="timeline-controls">
        <select v-model="selectedDays" @change="loadTimelineData" class="days-selector">
          <option value="7">{{ $t('Last7Days') }}</option>
          <option value="30">{{ $t('Last30Days') }}</option>
          <option value="90">{{ $t('Last90Days') }}</option>
          <option value="365">{{ $t('LastYear') }}</option>
        </select>
        <button @click="loadTimelineData" class="refresh-btn" :disabled="loading">
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
        </button>
      </div>
    </div>

    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>{{ $t('LoadingYourJourney') }}</p>
    </div>

    <div v-else-if="error" class="error-container">
      <i class="fas fa-exclamation-triangle"></i>
      <p>{{ error }}</p>
      <button @click="loadTimelineData" class="retry-btn">{{ $t('TryAgain') }}</button>
    </div>

    <div v-else-if="timelineEvents.length === 0" class="empty-container">
      <i class="fas fa-history"></i>
      <h3>{{ $t('NoActivityYet') }}</h3>
      <p>{{ $t('JourneyTimelineWillAppear') }}</p>
    </div>

    <div v-else class="timeline">
      <div class="timeline-line"></div>
      
      <div 
        v-for="(event, index) in timelineEvents" 
        :key="event.Id"
        class="timeline-item"
        :class="`timeline-item--${event.Color}`"
      >
        <div class="timeline-marker">
          <i :class="getIconClass(event.Icon)" :style="{ color: getColorValue(event.Color) }"></i>
          <span v-if="event.GroupedCount && event.GroupedCount > 1" class="timeline-count-badge">{{ event.GroupedCount }}</span>
        </div>
        
        <div class="timeline-content">
          <div class="timeline-content-header">
            <h4 class="timeline-title-text">{{ event.Title }}</h4>
            <div class="timeline-date-container">
              <span class="timeline-date">{{ formatDate(event.TimeStamp) }}</span>
              <span v-if="event.TimeRange" class="timeline-range">
                ({{ formatDate(event.TimeRange.last) }} - {{ formatDate(event.TimeRange.first) }})
              </span>
            </div>
          </div>
          
          <div class="timeline-content-body">
            <p class="timeline-description">{{ event.Description }}</p>
            
            <!-- Show step flag details if it's a flagged step -->
            <div v-if="event.EventType === 'step_flagged'" class="step-flag-details">
              <div class="flag-status" :class="{ 'resolved': event.Resolved }">
                <i :class="event.Resolved ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'"></i>
                <span>{{ event.Resolved ? $t('Resolved') : $t('PendingResolution') }}</span>
              </div>
              
              <div v-if="event.Reason" class="flag-reason">
                <strong>{{ $t('Reason') }}:</strong> {{ event.Reason }}
              </div>
              
              <div v-if="event.Resolved && event.ResolvedComment" class="resolution-comment">
                <strong>{{ $t('Resolution') }}:</strong> {{ event.ResolvedComment }}
                <span v-if="event.ResolvedBy" class="resolved-by">by {{ event.ResolvedBy }}</span>
              </div>
            </div>

            <!-- Show step completion details -->
            <div v-if="event.EventType === 'step_completed'" class="step-completion-details">
              <div class="completion-info">
                <i class="fas fa-check-circle"></i>
                <span>{{ $t('StepCompletedSuccessfully', { stepId: event.StepId }) }}</span>
              </div>
            </div>

            <!-- Show feedback details if it's a rating/feedback -->
            <div v-if="event.EventType === 'feedback'" class="feedback-details">
              <div class="rating-display">
                <div class="stars">
                  <i v-for="n in 5" :key="n" 
                     :class="n <= event.Rating ? 'fas fa-star' : 'far fa-star'"
                     :style="{ color: n <= event.Rating ? '#fbbf24' : '#d1d5db' }"></i>
                </div>
                <span class="rating-text">{{ event.Rating }}/5 {{ $t('Stars') }}</span>
              </div>
              
              <div v-if="event.Comment" class="feedback-comment">
                <strong>{{ $t('Comment') }}:</strong> {{ event.Comment }}
              </div>
              
              <div class="feedback-metrics">
                <div v-if="event.WasArticleHelpful" class="metric-item helpful">
                  <i class="fas fa-thumbs-up"></i>
                  <span>{{ $t('Helpful') }}</span>
                </div>
                <div v-if="event.DidArticleSaveTime" class="metric-item time-saved">
                  <i class="fas fa-clock"></i>
                  <span>{{ $t('SavedMinutes', { minutes: event.TimeSaved || 0 }) }}</span>
                </div>
                <div v-if="event.WasAlreadyKnown" class="metric-item known">
                  <i class="fas fa-check"></i>
                  <span>{{ $t('AlreadyKnewThis') }}</span>
                </div>
              </div>
            </div>
            
            <!-- Show onboarding link -->
            <div class="timeline-actions">
              <button 
                @click="viewOnboarding(event.OnboardingId)" 
                class="view-btn"
                :class="`view-btn--${event.Color}`"
              >
                <i class="fas fa-eye"></i>
                {{ $t('ViewSOP') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import TCVelikClient from '@/TCVelikClient/TCVelikClient.js';

export default {
  name: 'UserJourneyTimeline',
  data() {
    return {
      timelineEvents: [],
      loading: false,
      error: null,
      selectedDays: 90
    };
  },
  mounted() {
    this.loadTimelineData();
  },
  methods: {
    async loadTimelineData() {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await TCVelikClient.GetRelative(`/api/analytics/GetUserJourneyTimeline?days=${this.selectedDays}`);
        
        if (response.data && response.data.Events) {
          this.timelineEvents = this.groupSimilarEvents(response.data.Events);
        } else {
          this.timelineEvents = [];
        }
      } catch (error) {
        console.error('Error loading timeline data:', error);
        this.error = this.$t('FailedToLoadJourneyTimeline');
        this.timelineEvents = [];
      } finally {
        this.loading = false;
      }
    },
    
    groupSimilarEvents(events) {
      // Group events by OnboardingId and EventType
      const grouped = {};
      
      events.forEach(event => {
        const key = `${event.OnboardingId}_${event.EventType}`;
        
        if (!grouped[key]) {
          grouped[key] = {
            ...event,
            Count: 1,
            FirstOccurrence: event.TimeStamp,
            LastOccurrence: event.TimeStamp,
            AllOccurrences: [event.TimeStamp]
          };
        } else {
          grouped[key].Count++;
          grouped[key].AllOccurrences.push(event.TimeStamp);
          
          // Update first and last occurrence times
          const eventTime = new Date(event.TimeStamp);
          const firstTime = new Date(grouped[key].FirstOccurrence);
          const lastTime = new Date(grouped[key].LastOccurrence);
          
          if (eventTime < firstTime) {
            grouped[key].FirstOccurrence = event.TimeStamp;
          }
          if (eventTime > lastTime) {
            grouped[key].LastOccurrence = event.TimeStamp;
          }
        }
      });
      
      // Convert back to array and update titles/descriptions for grouped events
      const groupedEvents = Object.values(grouped).map(group => {
        if (group.Count > 1) {
          // Update title to show count
          const countText = group.Count > 1 ? ` (${group.Count} ${this.$t('Times')})` : '';
          
          let newTitle = group.Title;
          if (group.EventType === 'sop_opened') {
            newTitle = `${this.$t('Opened')} '${group.OnboardingName}'${countText}`;
          } else if (group.EventType === 'sop_completed') {
            newTitle = `${this.$t('Completed')} '${group.OnboardingName}'${countText}`;
          } else if (group.EventType === 'certificate_issued') {
            newTitle = `${this.$t('EarnedCertificateFor')} '${group.OnboardingName}'${countText}`;
          } else if (group.EventType === 'step_flagged') {
            newTitle = `${this.$t('FlaggedStepIn')} '${group.OnboardingName}'${countText}`;
          } else if (group.EventType === 'step_completed') {
            newTitle = `${this.$t('CompletedStepIn')} '${group.OnboardingName}'${countText}`;
          } else if (group.EventType === 'feedback') {
            newTitle = `${this.$t('Rated')} '${group.OnboardingName}'${countText}`;
          }
          
          return {
            ...group,
            Title: newTitle,
            TimeStamp: group.FirstOccurrence, // Use first occurrence for sorting
            GroupedCount: group.Count,
            TimeRange: group.Count > 1 ? {
              first: group.FirstOccurrence,
              last: group.LastOccurrence
            } : null
          };
        }
        
        return group;
      });
      
      // Sort by timestamp (most recent first)
      return groupedEvents.sort((a, b) => new Date(b.TimeStamp) - new Date(a.TimeStamp));
    },
    
    formatDate(dateString) {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = now - date; // Remove Math.abs to get proper direction
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) {
        return this.$t('Today');
      } else if (diffDays === 1) {
        return this.$t('Yesterday');
      } else if (diffDays < 7) {
        return this.$t('DaysAgo', { days: diffDays });
      } else if (diffDays < 30) {
        const weeks = Math.floor(diffDays / 7);
        return weeks > 1 ? this.$t('WeeksAgo', { weeks }) : this.$t('WeekAgo', { weeks });
      } else {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    },
    
    getIconClass(iconName) {
      const iconMap = {
        'play-circle': 'fas fa-play-circle',
        'check-circle': 'fas fa-check-circle',
        'award': 'fas fa-award',
        'flag': 'fas fa-flag',
        'star': 'fas fa-star',
        'circle': 'fas fa-circle'
      };
      return iconMap[iconName] || 'fas fa-circle';
    },
    
    getColorValue(colorName) {
      const colorMap = {
        'blue': '#3b82f6',
        'green': '#22c55e',
        'red': '#ef4444',
        'purple': '#8b5cf6',
        'yellow': '#f59e0b',
        'gray': '#6b7280'
      };
      return colorMap[colorName] || '#6b7280';
    },
    
    getEventCount(eventType) {
      return this.timelineEvents.filter(event => event.EventType === eventType).length;
    },
    
    viewOnboarding(onboardingId) {
      // Navigate to the onboarding view
      this.$router.push(`/pages/pdf-view?oid=${onboardingId}`);
    }
  }
};
</script>

<style scoped>
.timeline-container {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  margin-bottom: 24px;
}

.timeline-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
}

.timeline-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.timeline-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.days-selector {
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  font-size: 14px;
  color: #374151;
}

.refresh-btn {
  padding: 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
}

.refresh-btn:hover:not(:disabled) {
  background: #f9fafb;
  color: #374151;
}

.refresh-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.loading-container,
.error-container,
.empty-container {
  text-align: center;
  padding: 48px 24px;
  color: #6b7280;
}

.loading-container i,
.error-container i,
.empty-container i {
  font-size: 3rem;
  margin-bottom: 16px;
  color: #d1d5db;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f4f6;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-container {
  color: #dc2626;
}

.error-container i {
  color: #fca5a5;
}

.empty-container h3 {
  margin: 16px 0 8px;
  color: #374151;
}

.retry-btn {
  margin-top: 16px;
  padding: 8px 16px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.retry-btn:hover {
  background: #2563eb;
}

.timeline {
  position: relative;
  padding-left: 24px;
}

.timeline-line {
  position: absolute;
  left: 12px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: linear-gradient(to bottom, #e5e7eb, #d1d5db);
}

.timeline-item {
  position: relative;
  margin-bottom: 32px;
  padding-left: 32px;
}

.timeline-item:last-child {
  margin-bottom: 0;
}

.timeline-marker {
  position: absolute;
  left: -8px;
  top: 4px;
  width: 16px;
  height: 16px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  position: relative;
}

.timeline-marker i {
  font-size: 12px;
}

.timeline-count-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  background: #ef4444;
  color: white;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;
  border: 2px solid white;
}

.timeline-content {
  background: #f9fafb;
  border-radius: 8px;
  padding: 16px;
}


.timeline-content-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.timeline-title-text {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  flex: 1;
}

.timeline-date-container {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-left: 16px;
}

.timeline-date {
  font-size: 0.875rem;
  color: #6b7280;
  white-space: nowrap;
}

.timeline-range {
  font-size: 0.75rem;
  color: #9ca3af;
  white-space: nowrap;
  margin-top: 2px;
}

.timeline-description {
  color: #4b5563;
  margin: 0 0 12px 0;
  line-height: 1.5;
}

.step-flag-details {
  background: white;
  border-radius: 6px;
  padding: 12px;
  margin: 12px 0;
  border: 1px solid #e5e7eb;
}

.flag-status {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-weight: 500;
}

.flag-status.resolved {
  color: #22c55e;
}

.flag-status:not(.resolved) {
  color: #ef4444;
}

.flag-reason,
.resolution-comment {
  font-size: 0.875rem;
  color: #4b5563;
  margin-bottom: 4px;
}

.resolved-by {
  font-style: italic;
  color: #6b7280;
}

.step-completion-details {
  background: white;
  border-radius: 6px;
  padding: 12px;
  margin: 12px 0;
  border: 1px solid #e5e7eb;
}

.completion-info {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #22c55e;
  font-weight: 500;
}

.feedback-details {
  background: white;
  border-radius: 6px;
  padding: 12px;
  margin: 12px 0;
  border: 1px solid #e5e7eb;
}

.rating-display {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.stars {
  display: flex;
  gap: 2px;
}

.stars i {
  font-size: 14px;
}

.rating-text {
  font-weight: 500;
  color: #374151;
}

.feedback-comment {
  font-size: 0.875rem;
  color: #4b5563;
  margin-bottom: 8px;
  font-style: italic;
}

.feedback-metrics {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.metric-item {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.metric-item.helpful {
  background: #dcfce7;
  color: #166534;
}

.metric-item.time-saved {
  background: #dbeafe;
  color: #1e40af;
}

.metric-item.known {
  background: #f3f4f6;
  color: #374151;
}

.timeline-actions {
  margin-top: 12px;
}

.view-btn {
  padding: 6px 12px;
  border: 1px solid;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.view-btn--blue {
  border-color: #3b82f6;
  color: #3b82f6;
}

.view-btn--blue:hover {
  background: #3b82f6;
  color: white;
}

.view-btn--green {
  border-color: #22c55e;
  color: #22c55e;
}

.view-btn--green:hover {
  background: #22c55e;
  color: white;
}

.view-btn--red {
  border-color: #ef4444;
  color: #ef4444;
}

.view-btn--red:hover {
  background: #ef4444;
  color: white;
}

.view-btn--purple {
  border-color: #8b5cf6;
  color: #8b5cf6;
}

.view-btn--purple:hover {
  background: #8b5cf6;
  color: white;
}

.view-btn--yellow {
  border-color: #f59e0b;
  color: #f59e0b;
}

.view-btn--yellow:hover {
  background: #f59e0b;
  color: white;
}


@media (max-width: 768px) {
  .timeline-container {
    padding: 16px;
  }
  
  .timeline-header {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
  
  .timeline-controls {
    justify-content: space-between;
  }
  
  .timeline-content-header {
    flex-direction: column;
    gap: 8px;
  }
  
  .timeline-date-container {
    margin-left: 0;
    align-items: flex-start;
  }
}
</style>
